import { useMemo } from "react"
import { ColumnDef } from "@tanstack/react-table"
import { format } from "date-fns"
import { he } from "date-fns/locale"
import { Eye } from "lucide-react"
import { Link } from "react-router-dom"

import { Badge } from "@components/common/badge"
import RefreshUserInvite from "@components/users/refresh-user-invite"

import { DictType } from "@pma/types"
import { cn } from "@pma/utils/helpers"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { STATUS_VARIANT } from "@pma/utils/constants"
import useTranslator from "@hooks/use-translator"

export const useUsersColumns = (): ColumnDef<DictType>[] => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  return useMemo<ColumnDef<DictType>[]>(
    () => [
      {
        accessorKey: "name",
        header: () => translate("Name"),
        cell: ({ row }) => {
          const user = row.original
          const picture = user?.user_picture?.url
          return (
            <div className='flex items-center space-x-2'>
              <img
                alt=''
                src={`${picture || process.env.PUBLIC_URL + "/user-profile.svg"}`}
                className='h-[35px] w-[35px] rounded-full object-cover'
              />
              <div className='flex flex-col px-1'>
                <span className='max-w-[500px] truncate font-medium capitalize'>
                  {user?.name || "---"}
                </span>
                <small className='max-w-[500px] truncate lowercase text-slate-400'>
                  {user?.email}
                </small>
              </div>
            </div>
          )
        },
      },
      {
        accessorKey: "job_title",
        header: () => translate("Job Title"),
        cell: ({ row }) => (
          <span className='max-w-[500px] truncate font-medium capitalize'>
            {row.original?.job_title?.name || "---"}
          </span>
        ),
      },
      {
        accessorKey: "role",
        header: () => translate("Role"),
        cell: ({ row }) => {
          const role = row.original?.role
          return (
            <Badge
              variant={role?.name === "Admin" ? "premium" : "outline"}
              className='flex-col justify-start'
            >
              {role?.name || "---"}
            </Badge>
          )
        },
      },
      {
        accessorKey: "status",
        header: () => translate("Status"),
        cell: ({ row }) => {
          const user = row.original
          const status = user?.status
          const hasAcceptedInvite = user?._invitation?.is_accepted
          const statusBg = status?.name ? STATUS_VARIANT[status.name] || "" : ""
          return (
            <div className='flex flex-col items-center justify-center gap-2'>
              {status?.name && (
                <Badge
                  variant='outline'
                  className={cn("border-1 flex-col justify-start border-slate-400", statusBg)}
                >
                  {translate(status?.name)}
                </Badge>
              )}
              {!hasAcceptedInvite && (
                <small className='w-fit text-[10px] text-slate-500'>
                  {translate("Pending Invite")}
                </small>
              )}
            </div>
          )
        },
        meta: { className: "hidden md:table-cell" },
      },
      {
        accessorKey: "first_login_time",
        header: () => translate("Active Date"),
        cell: ({ row }) => (
          <span className='max-w-[500px] truncate font-medium capitalize'>
            {row.original?.first_login_time
              ? format(row.original.first_login_time, "dd MMM yyyy, HH:mm", {
                  locale: isHebrew ? he : undefined,
                })
              : "---"}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => translate("Actions"),
        cell: ({ row }) => {
          const user = row.original
          const hasAcceptedInvite = user?._invitation?.is_accepted
          return (
            <div className='flex items-center gap-2'>
              <RefreshUserInvite user={user} />
              {hasAcceptedInvite ? (
                <Link to={USERS_PAGE_ROUTE + `/${user.id}`}>
                  <Eye size={14} />
                </Link>
              ) : null}
            </div>
          )
        },
      },
    ],
    [translate, isHebrew],
  )
}
